import React from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import moment from "moment";
import { Box, Button, Chip, Link, Paper, Stack, Typography } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  CustomeDialog,
  DebouncedSearch,
  Loader,
  ServerPaginatedTable,
  useFormatAmount,
} from "../../../shared";
import { useFtiOrderList } from "../hooks";
import { FTI_STATUS_STYLES } from "../constants";
import { FtiBilingualText } from "./FtiBilingualText";
import { FtiDatePicker } from "./FtiDatePicker";

// A full tax invoice can only be issued for a completed order
const COMPLETED_ORDER_STATUS_ID = 4;

const TABLE_HEIGHT = "calc(100vh - 240px)";

const FtiOrderList = ({ requestPeriodDays, onSelectOrder }) => {
  const { state, minDate, gotoPage, setPageSize, handleFilterChange } =
    useFtiOrderList(requestPeriodDays);
  const formatAmount = useFormatAmount();
  const { pathname } = useLocation();
  const isStoreAdmin = pathname.includes("store-admin");

  const columns = React.useMemo(
    () => [
      {
        Header: <FtiBilingualText en="ABB Invoice No." th="เลขที่ใบกำกับภาษี ABB" />,
        accessor: "order_number",
        disableSortBy: true,
      },
      {
        Header: <FtiBilingualText en="Order Date" th="วันที่สั่งซื้อ" />,
        accessor: "order_date",
        disableSortBy: true,
        Cell: ({ value, row }) => (
          <FtiBilingualText en={value} th={row.original.display_order_date_tz} />
        ),
      },
      ...(isStoreAdmin
        ? []
        : [
            {
              Header: <FtiBilingualText en="Store Name" th="ชื่อร้านค้า" />,
              accessor: "store_name",
              disableSortBy: true,
              Cell: ({ value }) => value ?? "--",
            },
          ]),
      {
        Header: <FtiBilingualText en="Customer" th="ลูกค้า" />,
        accessor: "customer_name",
        disableSortBy: true,
        Cell: ({ value }) => value || "--",
      },
      {
        Header: <FtiBilingualText en="Grand Total" th="ยอดรวมสุทธิ" />,
        accessor: "grand_total",
        disableSortBy: true,
        Cell: ({ value }) => formatAmount(value),
      },
      {
        Header: <FtiBilingualText en="Status" th="สถานะ" />,
        accessor: "status",
        disableSortBy: true,
        // Same colour split as the sales log: statuses above 4 are cancelled / refunded
        Cell: ({ value, row }) =>
          value ? (
            <Chip
              size="small"
              label={value}
              sx={
                row.original.status_id > 4
                  ? FTI_STATUS_STYLES.cancelled
                  : FTI_STATUS_STYLES.issued
              }
            />
          ) : (
            "--"
          ),
      },
      {
        Header: "Action",
        accessor: "action",
        disableSortBy: true,
        align: "center",
        Cell: ({ row }) => {
          const { is_fti, fti = [], status_id } = row.original;
          // A cancelled FTI can sit next to its re-issued one, so link the issued (status 1) FTI
          const issuedFti = fti.find((item) => item.status === 1) ?? fti[0];
          const canCreate = !is_fti && status_id === COMPLETED_ORDER_STATUS_ID;

          return issuedFti ? (
            <Link
              component={RouterLink}
              to={`${
                isStoreAdmin ? "/store-admin" : ""
              }/full-tax-invoice/${issuedFti.id}/details`}
              target="_blank"
              rel="noopener noreferrer"
              underline="none"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                fontWeight: 500,
              }}
            >
              FTI: {issuedFti.invoice_number}
              <OpenInNewIcon sx={{ fontSize: 16 }} />
            </Link>
          ) : (
            <Button
              size="small"
              variant="outlined"
              disabled={!canCreate}
              onClick={() => onSelectOrder(row.original)}
              sx={{ whiteSpace: "nowrap" }}
            >
              {canCreate ? "Create/สร้าง FTI" : "Create FTI"}
            </Button>
          );
        },
      },
    ],
    [formatAmount, onSelectOrder, isStoreAdmin]
  );

  return (
    <Stack gap="16px">
      {/* On narrow screens the date picker squeezed the search input to nothing, so they stack */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        gap="16px"
      >
        <DebouncedSearch
          placeholder="Search..."
          value={state.search}
          onChange={(value) => handleFilterChange("search", value)}
        />
        <FtiDatePicker
          label="Date"
          value={state.date ? moment(state.date).toDate() : null}
          minDate={minDate ? moment(minDate).toDate() : undefined}
          onChange={(value) =>
            handleFilterChange(
              "date",
              value ? moment(value).format("YYYY-MM-DD") : null
            )
          }
          sx={{ minWidth: 200 }}
        />
      </Stack>
      {/* Only the rows scroll, the column headers and pagination stay in view.
          The min height keeps the dialog from shrinking when only a few rows come back */}
      <Box
        sx={{
          minHeight: TABLE_HEIGHT,
          "& thead": { position: "sticky", top: 0, zIndex: 1 },
          "& tfoot": {
            position: "sticky",
            bottom: -1,
            backgroundColor: "#fff",
          },
        }}
      >
        {/* The table's own loader is a short strip, this one fills the table area */}
        {state.isLoading ? (
          <Paper
            sx={{
              height: TABLE_HEIGHT,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "12px",
            }}
          >
            <Loader />
            <Typography sx={{ fontSize: "0.875rem", color: "#65748B" }}>
              Loading orders... / กำลังโหลดคำสั่งซื้อ...
            </Typography>
          </Paper>
        ) : (
          <ServerPaginatedTable
            columns={columns}
            data={state.orders}
            pagination={state.pagination}
            gotoPage={gotoPage}
            setPageSize={setPageSize}
            handleSort={() => {}}
            sortByGlobal={[]}
            searchBar={false}
            maxHeight={TABLE_HEIGHT}
          />
        )}
      </Box>
    </Stack>
  );
};

export const FtiOrderSelectionDialog = ({
  open,
  requestPeriodDays,
  onClose,
  onSelectOrder,
}) => {
  return (
    <CustomeDialog
      open={open}
      handleClose={onClose}
      title="Orders/คำสั่งซื้อ"
      customTitlePadding="4px 48px 16px 0"
      PaperProps={{
        sx: { width: "960px", maxWidth: "calc(100% - 32px)", p: "16px" },
      }}
      content={
        <FtiOrderList
          requestPeriodDays={requestPeriodDays}
          onSelectOrder={onSelectOrder}
        />
      }
    />
  );
};
